import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkIfOffRoute, calculateDistance, findNearestPointOnRoute, calculateBearing } from '../logic/NavigationHelpers';
import { addToast } from '../../ui/toastSlice';
import { useTranslation } from 'react-i18next';
import {
    setCurrentLocation,
    setCurrentBearing,
    setCurrentSpeed,
    selectRouteCoordinates,
    selectIsDrivingMode,
    selectIsRerouting,
    setIsDrivingMode,
    setIsRoutingMode,
} from '../mapSlice';
import { useRerouting } from './useRerouting';

/**
 * Custom hook to manage real GPS tracking for production navigation
 * Uses browser's Geolocation API to track user's actual location
 */
export const useGPSTracking = (checkProximity) => {
    const dispatch = useDispatch();
    const routeCoordinates = useSelector(selectRouteCoordinates);
    const isDrivingMode = useSelector(selectIsDrivingMode);
    const isRerouting = useSelector(selectIsRerouting);
    const { t } = useTranslation();

    const watchIdRef = useRef(null);
    const lastPositionRef = useRef(null);
    const arrivalTriggeredRef = useRef(false);

    const { updateDestination, triggerReroute } = useRerouting();

    const LOW_SPEED_THRESHOLD_KMH = 5;


    const startGPSTracking = () => {
        if (!routeCoordinates || !navigator.geolocation) return;

        // Store destination for rerouting
        updateDestination();

        // Reset arrival flag
        arrivalTriggeredRef.current = false;

        // Start watching position
        watchIdRef.current = navigator.geolocation.watchPosition(
            (position) => {
                const { latitude, longitude, speed: gpsSpeed, heading } = position.coords;
                const currentLoc = { lat: latitude, lng: longitude };

                // Convert speed from m/s to km/h (if available)
                const speedKmh = gpsSpeed !== null ? gpsSpeed * 3.6 : 0;

                // Calculate bearing from movement if heading not available
                let bearing = heading;

                // Low-speed stabilization: If moving slowly, use route direction instead of GPS bearing
                if (speedKmh < LOW_SPEED_THRESHOLD_KMH && routeCoordinates && routeCoordinates.length > 0) {
                    const { index } = findNearestPointOnRoute(currentLoc, routeCoordinates);
                    // Look ahead a few points to get a stable direction
                    // If near end, look to the very last point
                    const lookAheadIndex = Math.min(index + 5, routeCoordinates.length - 1);

                    if (lookAheadIndex > index) {
                        const targetPoint = routeCoordinates[lookAheadIndex];
                        bearing = calculateBearing(
                            currentLoc.lat,
                            currentLoc.lng,
                            targetPoint.lat,
                            targetPoint.lng
                        );
                    } else if (bearing === null && lastPositionRef.current) {
                        // Fallback to last position logic if at end of route
                        bearing = calculateBearing(
                            lastPositionRef.current.lat,
                            lastPositionRef.current.lng,
                            latitude,
                            longitude
                        );
                    }
                } else if (bearing === null && lastPositionRef.current) {
                    bearing = calculateBearing(
                        lastPositionRef.current.lat,
                        lastPositionRef.current.lng,
                        latitude,
                        longitude
                    );
                } else if (bearing === null) {
                    bearing = 0; // Default if no previous position
                }

                // Update state
                dispatch(setCurrentLocation(currentLoc));
                dispatch(setCurrentBearing(bearing));
                dispatch(setCurrentSpeed(speedKmh));
                checkProximity(currentLoc, bearing);

                // Check for arrival (within 30 meters of destination)
                if (routeCoordinates && routeCoordinates.length > 0) {
                    const destination = routeCoordinates[routeCoordinates.length - 1];
                    const distanceToDest = calculateDistance(
                        currentLoc.lat,
                        currentLoc.lng,
                        destination.lat,
                        destination.lng
                    );

                    if (distanceToDest < 20) {
                        if (arrivalTriggeredRef.current) return;

                        arrivalTriggeredRef.current = true;
                        dispatch(addToast({
                            message: t('hud.arrived'),
                            type: 'info',
                            duration: 5000
                        }));
                        dispatch(setIsDrivingMode(false));
                        dispatch(setIsRoutingMode(false));
                        return;
                    }
                }

                // Check if off route and trigger reroute
                if (!isRerouting && checkIfOffRoute(currentLoc, routeCoordinates)) {
                    triggerReroute(currentLoc, stopGPSTracking);
                }

                lastPositionRef.current = currentLoc;
            },
            (error) => {
                console.error('GPS tracking error:', error);
            },
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0
            }
        );
    };

    const stopGPSTracking = () => {
        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
            lastPositionRef.current = null;
        }
    };

    // Auto-start/stop GPS tracking based on driving mode
    useEffect(() => {
        if (isDrivingMode && routeCoordinates) {
            startGPSTracking();
        } else {
            stopGPSTracking();
        }

        return () => stopGPSTracking();
    }, [isDrivingMode, routeCoordinates]); // eslint-disable-line react-hooks/exhaustive-deps

    // Restart GPS tracking after rerouting
    useEffect(() => {
        if (isRerouting && routeCoordinates && isDrivingMode) {
            // New route found, restart GPS tracking
            stopGPSTracking();
            startGPSTracking();
        }
    }, [routeCoordinates]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        startGPSTracking,
        stopGPSTracking
    };
};
